import type { RouteRecordRaw } from 'vue-router';
import AdminTransactionsPage from '@/pages/admin/AdminTransactionsPage.vue';
import { useAuthStore } from '@/stores/auth';
import { useMerchantStore } from '@/stores/merchant';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/HomePage.vue'),
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () => import('@/pages/auth/AuthPage.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/auth/magic',
    name: 'AuthMagic',
    component: () => import('@/pages/auth/AuthMagicPage.vue'),
    meta: { requiresGuest: true },
  },
  // ==================== Membership Routes ====================
  {
    path: '/membership/card',
    name: 'MemberCard',
    component: () => import('@/pages/membership/MemberCardPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/join',
    name: 'JoinMembership',
    component: () => import('@/pages/membership/JoinPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/checkin',
    name: 'MembershipCheckIn',
    component: () => import('@/pages/membership/CheckInPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/credentials',
    name: 'MembershipCredentials',
    component: () => import('@/pages/membership/CredentialWalletPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/rewards',
    name: 'MembershipRewards',
    component: () => import('@/pages/membership/RewardsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/referral',
    name: 'MembershipReferral',
    component: () => import('@/pages/membership/ReferralPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/merchant/dashboard',
    name: 'MerchantDashboard',
    component: () => import('@/pages/membership/merchant/DashboardPage.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/membership/merchant/branch',
    name: 'MerchantBranchDashboard',
    component: () => import('@/pages/membership/merchant/BranchDashboardPage.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/membership/merchant/staff',
    name: 'MerchantStaffPage',
    component: () => import('@/pages/membership/merchant/StaffPage.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/membership/merchant/branch/:branchId/qr-display',
    name: 'MerchantBranchQRDisplay',
    component: () => import('@/pages/membership/merchant/QRDisplayPage.vue'),
    meta: { requiresAuth: true, requiresStaff: true },
  },
  {
    path: '/membership/register-merchant',
    name: 'RegisterMerchantPage',
    component: () => import('@/pages/membership/RegisterMerchantPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/membership/merchant/:merchantId/users',
    name: 'MerchantManageStaffPage',
    component: () => import('@/pages/membership/merchant/ManageStaffPage.vue'),
    meta: { requiresAuth: true, requiresMerchantAdmin: true },
  },
  {
    path: '/admin/merchants/approvals',
    name: 'MerchantApprovalPage',
    component: () => import('@/pages/admin/MerchantApprovalPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  // Ai mở nhầm URL API trên cùng origin (SPA) — chuyển về trang magic, giữ query token
  {
    path: '/api/auth/magic',
    redirect: (to) => ({ path: '/auth/magic', query: to.query }),
  },
  {
    path: '/auth/forgot-password',
    name: 'AuthForgotPassword',
    component: () => import('@/pages/auth/AuthForgotPasswordPage.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/auth/reset-password',
    name: 'AuthResetPassword',
    component: () => import('@/pages/auth/AuthResetPasswordPage.vue'),
    meta: { requiresGuest: true },
  },
  {
    path: '/api-docs',
    name: 'ApiDocs',
    component: () => import('@/pages/ApiDocsPage.vue'),
  },
  {
    path: '/gateway/guide',
    name: 'NFTGatewayGuide',
    component: () => import('@/pages/gateway/NFTGatewayGuidePage.vue'),
  },
  {
    path: '/events',
    name: 'Events',
    component: () => import('@/pages/events/EventsPage.vue'),
    // meta: { footerColor: 'bg-[#0b0b0f]/95' },
  },
  {
    path: '/events/create',
    name: 'PublicCreateEvent',
    component: () => import('@/pages/organizer/CreateEventModePage.vue'),
    meta: { requiresAuth: true, headerColor: "bg-black/20", footerColor: "bg-black/20" },
  },
  {
    path: '/events/workspace',
    name: 'EventWorkspaceSelect',
    component: () => import('@/pages/events/WorkspaceSelectPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/events/create/quick',
    name: 'PublicQuickCreateEvent',
    component: () => import('@/pages/organizer/QuickCreateEventPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/events/create/form',
    name: 'PublicCreateEventForm',
    component: () => import('@/pages/organizer/CreateEventPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/primary',
    name: 'Primary',
    component: () => import('@/pages/primary/PrimaryPage.vue'),
  },
  {
    path: '/marketplace',
    name: 'Marketplace',
    component: () => import('@/pages/marketplace/MarketplacePage.vue'),
  },
  {
    path: '/collections',
    name: 'Collections',
    component: () => import('@/pages/collections/CollectionsPage.vue'),
  },
  {
    path: '/events/:id',
    name: 'EventDetail',
    component: () => import('@/pages/events/EventDetailPage.vue'),
    props: true,
  },
  {
    path: '/events/slug/:slug',
    name: 'EventDetailBySlug',
    component: () => import('@/pages/events/EventDetailPage.vue'),
    props: true,
  },
  {
    path: '/events/:eventId/purchase',
    name: 'PurchaseTicket',
    component: () => import('@/pages/tickets/PurchaseTicketPage.vue'),
    props: true,
  },
  {
    path: '/marketplace/listings',
    name: 'MarketplaceListings',
    component: () => import('@/pages/marketplace/ListingsPage.vue'),
  },
  {
    path: '/marketplace/listings/:id',
    name: 'ListingDetail',
    component: () => import('@/pages/marketplace/ListingDetailPage.vue'),
    props: true,
  },
  {
    path: '/marketplace/listings/:id/edit',
    name: 'EditListing',
    component: () => import('@/pages/marketplace/EditListingPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/marketplace/my-listings',
    name: 'MarketplaceMyListings',
    component: () => import('@/pages/marketplace/MyListingsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/marketplace/collections',
    name: 'MarketplaceCollections',
    component: () => import('@/pages/marketplace/CollectionsPage.vue'),
  },
  {
    path: '/marketplace/collections/:id',
    name: 'CollectionDetail',
    component: () => import('@/pages/marketplace/CollectionDetailPage.vue'),
    props: true,
  },
  {
    path: '/auctions',
    name: 'AuctionsAlias',
    redirect: { name: 'Auctions' },
  },
  {
    path: '/marketplace/auctions',
    name: 'Auctions',
    component: () => import('@/pages/marketplace/AuctionsPage.vue'),
  },
  {
    path: '/marketplace/auctions/:id',
    name: 'AuctionDetail',
    component: () => import('@/pages/marketplace/AuctionDetailPage.vue'),
    props: true,
  },
  {
    path: '/nft',
    name: 'NFT',
    component: () => import('@/pages/nft/NFTManagementPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/mint',
    name: 'NFTMinting',
    component: () => import('@/pages/nft/NFTMintingPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/:id',
    name: 'NFTDetail',
    component: () => import('@/pages/nft/NFTDetailPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/owner/:ownerAddress',
    name: 'NFTOwner',
    component: () => import('@/pages/nft/NFTOwnerPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/contract/:contractAddress',
    name: 'NFTContract',
    component: () => import('@/pages/nft/NFTContractPage.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/search',
    name: 'NFTSearch',
    component: () => import('@/pages/nft/NFTSearchPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/collections',
    name: 'NFTCollections',
    component: () => import('@/pages/nft/NFTCollectionsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/nft/analytics',
    name: 'NFTAnalytics',
    component: () => import('@/pages/nft/NFTAnalyticsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import('@/pages/account/AccountPage.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'AccountDashboard',
        component: () => import('@/pages/account/DashboardPage.vue'),
      },
      {
        path: 'tickets',
        name: 'MyTickets',
        component: () => import('@/pages/account/MyTicketsPage.vue'),
      },
      {
        path: 'tickets/transfer',
        name: 'TicketTransfer',
        component: () => import('@/pages/tickets/TicketTransferPage.vue'),
      },
      {
        path: 'refunds',
        name: 'RefundRequests',
        component: () => import('@/pages/account/RefundRequestsPage.vue'),
      },
      {
        path: 'wallet',
        name: 'Wallet',
        component: () => import('@/pages/wallet/WalletPage.vue'),
      },
      {
        path: 'attendance',
        name: 'AccountAttendance',
        // @ts-ignore - .vue file resolution is handled by Vite/Vue tooling
        component: () => import('../pages/account/AttendancePage.vue'),
      },
      {
        path: 'history',
        name: 'History',
        component: () => import('@/pages/account/HistoryPage.vue'),
      },
      {
        path: 'listings',
        name: 'AccountMyListings',
        component: () => import('@/pages/account/MyListingsPage.vue'),
      },
      {
        path: 'auctions',
        name: 'MyAuctions',
        component: () => import('@/pages/account/MyAuctionsPage.vue'),
      },
      {
        path: 'events',
        name: 'MyEvents',
        component: () => import('@/pages/account/MyEventsPage.vue'),
      },
      {
        path: 'media',
        name: 'MediaManagement',
        component: () => import('@/pages/media/MediaManagementPage.vue'),
      },
      {
        path: 'gifts',
        name: 'MyGifts',
        component: () => import('@/pages/account/MyGiftsPage.vue'),
      },
      {
        path: 'verify',
        name: 'VerifyAccount',
        component: () => import('@/pages/account/VerifyAccountPage.vue'),
      },
    ],
  },
  {
    path: '/organizer',
    name: 'Organizer',
    component: () => import('@/pages/organizer/OrganizerPage.vue'),
    meta: { requiresAuth: true, requiresOrganizer: true },
    children: [
      {
        path: '',
        name: 'OrganizerDashboard',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/organizer/DashboardPage.vue'),
      },
      {
        path: 'account-settings',
        name: 'OrganizerAccountSettings',
        component: () => import('@/pages/organizer/AccountSettingsPage.vue'),
      },
      {
        path: 'events',
        name: 'ManageEvents',
        component: () => import('@/pages/organizer/ManageEventsPage.vue'),
      },
      {
        path: 'events/create',
        name: 'CreateEvent',
        component: () => import('@/pages/organizer/CreateEventModePage.vue'),
        // meta: { hideHeader: true },
      },
      {
        path: 'events/create/quick',
        name: 'OrganizerQuickCreateEvent',
        component: () => import('@/pages/organizer/QuickCreateEventPage.vue'),
        // meta: { hideHeader: true },
      },
      {
        path: 'events/create/form',
        name: 'CreateEventForm',
        component: () => import('@/pages/organizer/CreateEventPage.vue'),
      },
      {
        path: 'events/:id/edit',
        name: 'EditEvent',
        component: () => import('@/pages/organizer/EditEventPage.vue'),
        props: true,
      },
      {
        path: 'events/:id/tickets',
        name: 'EventTicketTypes',
        component: () => import('@/pages/organizer/EventTicketTypesPage.vue'),
        props: true,
      },
      {
        path: 'campaigns',
        name: 'CampaignList',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/caimpaign/CampaignListPage.vue'),
      },
      {
        path: 'events/:eventId/campaign/create',
        name: 'EventCampaignCreate',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/caimpaign/EventCampaignFormPage.vue'),
        props: true,
      },
      {
        path: 'events/:eventId/campaign/:campaignId/edit',
        name: 'EventCampaignEdit',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/caimpaign/EventCampaignFormPage.vue'),
        props: true,
      },
      {
        path: 'gifts',
        name: 'ManageGifts',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/organizer/ManageGiftsPage.vue'),
      },
      {
        path: 'reports',
        name: 'Reports',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/organizer/ReportsPage.vue'),
      },
      {
        path: 'analytics',
        name: 'OrganizerAnalytics',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/organizer/AnalyticsPage.vue'),
      },
      {
        path: 'events/:id/reconciliation',
        name: 'EventReconciliation',
        component: () => import('@/pages/organizer/EventReconciliationPage.vue'),
        props: true,
      },
      {
        path: 'media',
        name: 'OrganizerMedia',
        meta: { fullOrganizerOnly: true },
        component: () => import('@/pages/organizer/MediaPage.vue'),
      },
    ],
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/layouts/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'AdminHome',
        component: () => import('@/pages/admin/AdminHomePage.vue'),
      },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('@/pages/admin/DashboardPage.vue'),
      },
      {
        path: 'events',
        name: 'AdminEvents',
        component: () => import('@/pages/admin/AdminEventsPage.vue'),
      },
      {
        path: 'events/approval',
        name: 'AdminEventApproval',
        component: () => import('@/pages/admin/AdminEventApprovalPage.vue'),
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('@/pages/admin/AdminUsersPage.vue'),
      },
      {
        path: 'transactions',
        name: 'AdminTransactions',
        component: AdminTransactionsPage,
      },
      {
        path: 'events/create',
        name: 'AdminCreateEvent',
        component: () => import('@/pages/organizer/CreateEventModePage.vue'),
        // meta: { hideHeader: true },
      },
      {
        path: 'events/create/quick',
        name: 'AdminQuickCreateEvent',
        component: () => import('@/pages/organizer/QuickCreateEventPage.vue'),
        // meta: { hideHeader: true },
      },
      {
        path: 'events/create/form',
        name: 'AdminCreateEventForm',
        component: () => import('@/pages/organizer/CreateEventPage.vue'),
      },
      {
        path: 'events/:id/edit',
        name: 'AdminEditEvent',
        component: () => import('@/pages/organizer/EditEventPage.vue'),
        props: true,
        meta: { isAdminPage: true },
      },
      {
        path: 'reports',
        name: 'AdminReports',
        component: () => import('@/pages/organizer/ReportsPage.vue'),
        meta: { isAdminPage: true },
      },
      {
        path: 'gifts',
        name: 'AdminGifts',
        component: () => import('@/pages/admin/AdminGiftsPage.vue'),
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('@/pages/admin/AdminSettingsPage.vue'),
      },
      {
        path: 'demo',
        name: 'AdminDemo',
        component: () => import('@/pages/admin/AdminDemoPage.vue'),
      },
      {
        path: 'layout-test',
        name: 'AdminLayoutTest',
        component: () => import('@/pages/admin/AdminLayoutTestPage.vue'),
      },
      {
        path: 'notifications',
        name: 'AdminNotifications',
        component: () => import('@/pages/admin/AdminNotificationsPage.vue'),
      },
      {
        path: 'system-config',
        name: 'AdminSystemConfig',
        component: () => import('@/pages/admin/AdminSystemConfigPage.vue'),
      },
      {
        path: 'media',
        name: 'AdminMedia',
        component: () => import('@/pages/admin/AdminMediaPage.vue'),
      },
      {
        path: 'audit',
        name: 'AdminAudit',
        component: () => import('@/pages/admin/AdminAuditPage.vue'),
      },
    ],
  },
  {
    path: '/check-in/:eventId?',
    name: 'CheckIn',
    component: () => import('@/pages/CheckInPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/about/project',
    name: 'AboutProject',
    component: () => import('@/pages/info/ProjectPage.vue'),
  },
  {
    path: '/about/team',
    name: 'AboutTeam',
    component: () => import('@/pages/info/TeamPage.vue'),
  },
  {
    path: '/about/recruitment',
    name: 'AboutRecruitment',
    component: () => import('@/pages/info/RecruitmentPage.vue'),
  },
  {
    path: '/about/faq',
    name: 'AboutFAQ',
    component: () => import('@/pages/info/FAQPage.vue'),
  },
  {
    path: '/about/terms',
    name: 'AboutTerms',
    component: () => import('@/pages/info/TermsPage.vue'),
  },
  {
    path: '/about/privacy',
    name: 'AboutPrivacy',
    component: () => import('@/pages/info/PrivacyPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
];

export default routes;

// Router guards
export function setupRouterGuards(router: any) {
  router.beforeEach((to: any, from: any, next: any) => {
    const authStore = useAuthStore();

    // Organizer account opening "/" should land in organizer area
    // if (to.path === '/' && (authStore.isOrganizer || authStore.isAdmin)) {
    //   if(authStore.isAdmin) {
    //     next('/admin');
    //     return
    //   }
    //   if(authStore.isOrganizer) {
    //     next('/organizer');
    //     return;
    //   }
    // }

    // Check if route requires authentication
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next('/auth');
      return;
    }

    // Check if route requires admin role
    if (to.meta.requiresAdmin && !authStore.isAdmin) {
      next('/'); // Redirect to home if not admin
      return;
    }

    // Check if route requires merchant admin role
    if (to.meta.requiresMerchantAdmin && authStore.activeContext?.activeRole !== 'MERCHANT_ADMIN') {
      next('/');
      return;
    }

    // Khu organizer: ADMIN, ORGANIZER/KYC, hoặc tài khoản FULL
    if (to.meta.requiresOrganizer && !authStore.canAccessOrganizerArea) {
      next('/');
      return;
    }

    // Chỉ admin / organizer role: Tổng quan, chiến dịch, báo cáo, ...
    const needsFullOrganizer = to.matched.some(
      (r) => r.meta.fullOrganizerOnly === true,
    );
    if (needsFullOrganizer && !authStore.hasFullOrganizerNav) {
      next({ path: '/organizer/events', replace: true });
      return;
    }

    // Magic link must always load so we can consume token (replace session), even if already logged in
    const isMagicLinkConsume =
      to.path === '/auth/magic' && typeof to.query?.token === 'string' && to.query.token.length > 0

    // Check if route requires staff authentication (merchant staff)
    if (to.meta.requiresStaff) {
      const merchantStore = useMerchantStore();
      const hasStaffRole = authStore.activeContext && 
        ['MERCHANT_ADMIN', 'BRANCH_MANAGER', 'STAFF'].includes(authStore.activeContext.activeRole);
      
      if (!merchantStore.isStaffAuthenticated && !hasStaffRole) {
        next('/auth'); // Redirect to auth if not staff
        return;
      }
    }

    // Check if route requires guest (not authenticated)
    if (to.meta.requiresGuest && authStore.isAuthenticated) {
      next('/'); // Redirect to home if already authenticated
      return;
    }

    next();
  });
}
